import { Typography, DialogTitle, IconButton, DialogContent, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import { SafeLeaseSelect } from "@safelease/components";
import { useRef, useState } from "react";
import dayjs from "dayjs";
import ThemedButton from "../../../settings/components/OrganizationSettings/OrganizationDialog/shared/ThemedButton";
import { reportTypes } from "./fmsActivityDefinitions";
import {
  BootstrapDialog,
  DatePickerLabel,
  ReportTypeContainer,
  StyledDivider,
  DownloadContainer,
  DownloadInfo,
} from "./fmsActivityDefinitions";
import { useQuery } from "@tanstack/react-query";
import { fmsReportsApi } from "../../../utilities/apiInstances/fms-reports.api";
import { ReportType } from "./fmsActivityDefinitions";

interface FetchedReport {
  downloadUrl: string;
  fileName: string;
  fileType: string;
}

interface FmsActivityDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedLocation: {
    id: number;
    locationId: number;
    relationshipId: number;
    llcId: number;
    llcName: string;
    locationName: string;
  };
  startServicePeriod: Date;
}

function FmsActivityDialog({ open, handleClose, selectedLocation, startServicePeriod }: FmsActivityDialogProps) {
  const [reportType, setReportType] = useState<string>(ReportType.INSURANCE_ACTIVITY_REPORT);
  const [fetchedReport, setFetchedReport] = useState<FetchedReport | null>(null);
  const reportStartDate = dayjs(startServicePeriod).startOf("month").format("YYYY-MM-DD");
  const reportEndDate = dayjs(reportStartDate).endOf("month").format("YYYY-MM-DD");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Format the service period for display
  const servicePeriodDisplay = `${reportStartDate} - ${reportEndDate}`;

  const { refetch: fetchReport, isError: isFetchError } = useQuery({
    queryKey: ["fms-report-download-url", selectedLocation?.id, reportType, reportStartDate, reportEndDate],
    queryFn: () => {
      if (!selectedLocation) return null;
      return fmsReportsApi.getReportDownloadUrl({
        locationId: selectedLocation.id,
        reportType,
        reportStartDate,
        reportEndDate,
      });
    },
    enabled: false, // only fetch if the user clicks the fetch report button
    retry: false, // dont automatically retry if the query fails. By default, react query will retry the query 3 times
    onSuccess: (data) => {
      if (!data) return;
      setFetchedReport({
        downloadUrl: data.downloadUrl,
        fileName: data.fileName,
        fileType: data.fileType,
      });
      setErrorMessage(null);
    },
    onError: (error: any) => {
      if (error?.response?.status === 404) {
        setErrorMessage("Report not found for the selected period.");
      } else {
        setErrorMessage("Error generating report. Please try again.");
      }
    },
  });

  if (!selectedLocation) {
    return null;
  }

  const handleReportTypeChange = (event: any) => {
    setReportType(event.target.value as string);
  };

  const handleFetchReport = async () => {
    if (!reportStartDate || !reportEndDate || !reportType) {
      return;
    }

    // Fetch the report
    await fetchReport();
  };

  const handleDownload = () => {
    if (fetchedReport) {
      window.open(fetchedReport.downloadUrl, "_blank");
    }
  };

  const clearFields = () => {
    setFetchedReport(null);
  };

  const handleCloseDialog = () => {
    clearFields();
    handleClose();
  };

  const reportTypeMenuItems = reportTypes.map((reportType) => {
    const isEnabled = reportType.value === "facilityReports.insuranceActivity-SS:dtInsuranceTable";

    return (
      <MenuItem key={reportType.value} value={reportType.value} disabled={!isEnabled}>
        {reportType.label}
      </MenuItem>
    );
  });

  return (
    <BootstrapDialog
      onClose={handleCloseDialog}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "600px",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          fontColor: "#000000",
          fontWeight: 700,
          fontSize: "20px",
          fontFamily: "Open Sans",
          lineHeight: "27px",
        }}
      >
        Fetch Stored FMS Report
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCloseDialog}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <StyledDivider />

      <DialogContent>
        <Typography
          sx={{
            fontColor: "#031E30",
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "19px",
            fontFamily: "Open Sans",
          }}
          gutterBottom
        >
          How it works
        </Typography>
        <Typography
          sx={{
            fontColor: "#929292",
            fontSize: "12px",
            fontWeight: 500,
            lineHeight: "16px",
            fontFamily: "Open Sans",
            opacity: "0.7",
            mt: 1.5,
            mb: 3,
          }}
          gutterBottom
        >
          Click Fetch Report to retrieve the Insurance Activity Report for the selected location and service period. The system will attempt
          to find a report that covers the entire month (starting on the 1st and ending on the last day). If no full-month report is found,
          nothing will be available for download and an error message will be displayed.
        </Typography>
        <Typography mb={2}>
          <strong>Selected Location: </strong>
          {selectedLocation.locationName} ({selectedLocation.id})
        </Typography>

        <Typography mb={2}>
          <strong>Service Period: </strong>
          {servicePeriodDisplay}
        </Typography>

        <ReportTypeContainer>
          <DatePickerLabel>Report Type</DatePickerLabel>
          <SafeLeaseSelect
            value={reportType}
            onChange={handleReportTypeChange}
            startAdornment={<DescriptionIcon sx={{ mr: 1, color: "text.secondary" }} />}
            sx={{
              height: 36,
              borderColor: "#EBEFF7 !important",
              "& fieldset": { border: "none" },
              width: "100%",
            }}
          >
            {reportTypeMenuItems}
          </SafeLeaseSelect>
        </ReportTypeContainer>

        {isFetchError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        )}

        {fetchedReport ? (
          <DownloadContainer>
            <DownloadInfo>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Report Fetched Successfully
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fetchedReport.fileName}
              </Typography>
            </DownloadInfo>
            <ThemedButton
              dark
              onClick={handleDownload}
              styles={{
                minWidth: "120px",
              }}
            >
              Download
            </ThemedButton>
          </DownloadContainer>
        ) : (
          <ThemedButton
            dark
            onClick={handleFetchReport}
            styles={{
              marginTop: "24px",
              width: "100%",
            }}
          >
            Fetch Report
          </ThemedButton>
        )}
      </DialogContent>
    </BootstrapDialog>
  );
}

export { FmsActivityDialog };
